/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

/**
 * Route → help key. The key matches the guiKey a route already declares wherever one exists, so the
 * help card, the sidebar entry and the Page Visibility switch all speak the same name.
 *
 * Order matters: the first pattern that matches wins, so put specific paths before their prefixes.
 * Anything unmatched falls back to `gettingStarted`, which is the one card that is useful everywhere.
 */
const ROUTE_HELP_KEYS: ReadonlyArray<[RegExp, string]> = [
  // vehicles
  [/^\/all-vehicles\/new$/, 'addVehicle'],
  [/^\/all-vehicles\/[^/]+\/gps$/, 'gpsTracking'],
  [/^\/all-vehicles\/[^/]+$/, 'addVehicle'],
  [/^\/all-vehicles$/, 'allVehicles'],
  [/^\/car-sharing/, 'carSharing'],
  [/^\/commercial-cars/, 'commercialCars'],
  [/^\/license-plates/, 'licensePlates'],
  [/^\/rates$/, 'rates'],
  [/^\/pricing-seasons$/, 'pricingSeasons'],
  [/^\/gps-tracking$/, 'gpsTracking'],

  // bookings
  [/^\/bookings$/, 'bookings'],
  [/^\/p2p-bookings\//, 'pendingBookings'],

  // tolls, violations, money
  [/^\/toll-provider\//, 'tollProviderAccount'],
  [/^\/toll-transactions$/, 'tollTransactions'],
  [/^\/payments\/tolls$/, 'tolls'],
  [/^\/payments\/violations$/, 'violations'],
  [/^\/payments\/charges$/, 'charges'],
  [/^\/payments\/invoices$/, 'driverInvoices'],
  [/^\/payments\/collections$/, 'collections'],
  [/^\/payments\/failed$/, 'failedPayments'],
  [/^\/payments\/successful$/, 'successfulPayments'],
  [/^\/payments\/stripe-dashboard$/, 'stripeDashboard'],
  [/^\/violations$/, 'violations'],
  [/^\/renter-tolls$/, 'renterTolls'],
  [/^\/driver-cards$/, 'driverCards'],
  [/^\/stripe-cards$/, 'banking'],
  [/^\/billing\/invoices$/, 'invoicesToPay'],
  [/^\/disputes/, 'disputes'],

  // people
  [/^\/users$/, 'users'],
  [/^\/create-user/, 'createUser'],
  [/^\/create-owner/, 'createOwner'],
  [/^\/create-partner/, 'createPartner'],
  [/^\/partners$/, 'partners'],
  [/^\/owners\/[^/]+$/, 'tollDistributions'],
  [/^\/owners$/, 'owners'],

  // company
  [/^\/download$/, 'download'],
  [/^\/guides$/, 'guides'],
  [/^\/locations$/, 'locations'],
  [/^\/settings$/, 'settings'],
  [/^\/design$/, 'design'],
  [/^\/messages$/, 'messages'],
  [/^\/sms-log$/, 'smsLog'],
  [/^\/reports\/account-statement$/, 'accountStatementReport'],
  [/^\/reports\/annual$/, 'annualReport'],

  // administration
  [/^\/admin\/settings$/, 'adminSettings'],
  [/^\/admin\/locations$/, 'commonLocations'],
  [/^\/admin\/plugins-settings$/, 'pluginsSettings'],
  [/^\/admin\/plugins$/, 'plugins'],
  [/^\/admin\/scrape-worker$/, 'scrapeWorker'],
  [/^\/admin\/platform-invoices$/, 'platformInvoices'],
  [/^\/admin\/platform-stripe-dashboard$/, 'platformStripeDashboard'],
  [/^\/admin\/reports\/account-statement$/, 'accountStatementReport'],
  [/^\/admin\/reports\/annual$/, 'annualReport'],

  // dashboard last: '/' would otherwise swallow nothing, but keep the intent explicit
  [/^\/$/, 'dashboard'],
];

/**
 * Where each help key actually lives — the inverse of the table above, which regular expressions
 * cannot give us. The assistant answers with help keys; this is what turns one into a "take me
 * there" button.
 *
 * `guiKey` is checked against the owner's Page Visibility settings and `adminOnly` against their
 * role, so the panel never offers a page that would bounce them back. Keys absent from this map are
 * pages that only exist under a specific record (a toll account, one owner's distribution settings)
 * — there is no address to send anybody to, so no button is offered for them.
 */
export interface HelpRoute {
  path: string;
  /** Page Visibility key. Defaults to the help key itself, which is usually the same name. */
  guiKey?: string;
  adminOnly?: boolean;
}

export const HELP_KEY_ROUTES: Readonly<Record<string, HelpRoute>> = {
  dashboard: { path: '/' },

  // vehicles
  allVehicles: { path: '/all-vehicles' },
  addVehicle: { path: '/all-vehicles/new', guiKey: 'allVehicles' },
  carSharing: { path: '/car-sharing' },
  commercialCars: { path: '/commercial-cars' },
  licensePlates: { path: '/license-plates' },
  rates: { path: '/rates' },
  pricingSeasons: { path: '/pricing-seasons' },
  gpsTracking: { path: '/gps-tracking' },

  // bookings
  bookings: { path: '/bookings' },

  // tolls, violations, money
  tolls: { path: '/payments/tolls' },
  tollTransactions: { path: '/toll-transactions' },
  violations: { path: '/violations' },
  charges: { path: '/payments/charges' },
  failedPayments: { path: '/payments/failed' },
  successfulPayments: { path: '/payments/successful' },
  stripeDashboard: { path: '/payments/stripe-dashboard' },
  disputes: { path: '/disputes' },
  collections: { path: '/payments/collections' },
  // driverInvoices is deliberately absent: /payments/invoices bills ONE driver and is opened
  // from that driver's menu with their id. A button leading there with nobody selected would
  // land the reader on an empty page, which is worse than no button.
  // Connecting an agency is a tab inside Company -> Settings, and the agency articles are reference
  // text with no screen of their own — the assistant can still offer the settings page for both.
  collectionsAgencies: { path: '/settings', guiKey: 'settings' },
  renterTolls: { path: '/renter-tolls' },
  driverCards: { path: '/driver-cards' },
  banking: { path: '/stripe-cards' },
  invoicesToPay: { path: '/billing/invoices' },

  // people
  users: { path: '/users' },
  createUser: { path: '/create-user', guiKey: 'users' },
  owners: { path: '/owners', guiKey: 'tollDistributions' },
  partners: { path: '/partners' },
  createOwner: { path: '/create-owner', adminOnly: true },
  createPartner: { path: '/create-partner', adminOnly: true },

  // company
  // No Page Visibility entry of its own — it is a list of downloads, open to every signed-in user.
  download: { path: '/download' },
  // Same as download: a list of files, open to every signed-in user, with no visibility key.
  guides: { path: '/guides' },
  locations: { path: '/locations' },
  settings: { path: '/settings' },
  design: { path: '/design' },
  messages: { path: '/messages' },
  smsLog: { path: '/sms-log' },
  accountStatementReport: { path: '/reports/account-statement' },
  annualReport: { path: '/reports/annual' },

  // administration
  adminSettings: { path: '/admin/settings', adminOnly: true },
  commonLocations: { path: '/admin/locations', adminOnly: true },
  plugins: { path: '/admin/plugins', adminOnly: true },
  pluginsSettings: { path: '/admin/plugins-settings', adminOnly: true },
  scrapeWorker: { path: '/admin/scrape-worker', adminOnly: true },
  platformInvoices: { path: '/admin/platform-invoices', adminOnly: true },
  platformStripeDashboard: { path: '/admin/platform-stripe-dashboard', adminOnly: true },
};

/** The card offered from every page as "new here? start with this". */
export const GETTING_STARTED_KEY = 'gettingStarted';

export function helpKeyForPath(pathname: string): string {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  for (const [pattern, key] of ROUTE_HELP_KEYS) {
    if (pattern.test(path)) return key;
  }
  return GETTING_STARTED_KEY;
}
